'use client';

import { useIntl } from 'react-intl';
import IconButton from '@alltrails/denali/components/IconButton';
import Print from '@alltrails/denali/icons/Print';
import type { AllTrailsBounds } from '@alltrails/maps/types/Geo';
import usePrintModal from '../../hooks/usePrintModal';
import type { PrintSubject } from '../../types/PrintSubject';
import type { PrintHost } from '../PrintModal/PrintModal';

type PrintMapButtonProps = {
  /** Frames the print. Null while the page has nothing printable yet, which renders no button. */
  bounds: AllTrailsBounds | null;
  autoOpen?: boolean;
  host: PrintHost;
  onClose?: () => void;
  onPrintRequested?: () => void;
  subject: PrintSubject;
};

/**
 * The map's print control: the button and the modal it opens. Hosts supply what to print (bounds,
 * subject) and what the page contributes (host), and own nothing else. A host with no room for a
 * button drives usePrintModal directly instead.
 */
const PrintMapButton = ({ autoOpen, bounds, host, onClose, onPrintRequested, subject }: PrintMapButtonProps) => {
  const intl = useIntl();
  const { isLoading, modal, open } = usePrintModal({ autoOpen, bounds, host, onClose, onPrintRequested, subject });

  if (!bounds) {
    return modal;
  }

  return (
    <>
      <IconButton
        elevated
        icon={{ Component: Print }}
        loading={isLoading}
        onClick={open}
        title={intl.formatMessage({ defaultMessage: 'Print map' })}
        variant="ghost"
      />
      {modal}
    </>
  );
};

export default PrintMapButton;
